import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import FieldEditor from './FieldEditor';
import api from '../../utils/api';
import {
  Box,
  Button,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Save as SaveIcon
} from '@mui/icons-material';

const FIELD_TYPES = ['text', 'number', 'phone', 'email', 'dropdown', 'yesno', 'multientry', 'image', 'textarea'];

const FormBuilder = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    formName: '',
    description: '',
    status: 'draft',
    fields: []
  });

  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (id) {
      fetchForm();
    }
  }, [id]);

  const fetchForm = async () => {
    try {
      const response = await api.get(`/forms/${id}`);
      setFormData(response.data.form);
    } catch (error) {
      showSnackbar(t('common.error'), 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddField = () => {
    setEditingField(null);
    setEditingIndex(null);
    setFieldDialogOpen(true);
  };

  const handleEditField = (index) => {
    setEditingField(formData.fields[index]);
    setEditingIndex(index);
    setFieldDialogOpen(true);
  };

  const handleSaveField = (fieldData) => {
    const newFields = [...formData.fields];

    if (editingIndex !== null) {
      newFields[editingIndex] = fieldData;
    } else {
      newFields.push(fieldData);
    }

    setFormData({ ...formData, fields: newFields });
    setFieldDialogOpen(false);
    setEditingField(null);
    setEditingIndex(null);
  };

  const handleDeleteField = (index) => {
    const newFields = formData.fields.filter((_, i) => i !== index);
    setFormData({ ...formData, fields: newFields });
  };

  const handleMoveField = (index, direction) => {
    const newFields = [...formData.fields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex >= 0 && targetIndex < newFields.length) {
      [newFields[index], newFields[targetIndex]] = [newFields[targetIndex], newFields[index]];
      setFormData({ ...formData, fields: newFields });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.formName) {
      showSnackbar('Form name is required', 'error');
      return;
    }

    if (formData.fields.length === 0) {
      showSnackbar('Please add at least one field', 'error');
      return;
    }

    try {
      if (id) {
        await api.put(`/forms/${id}`, formData);
        showSnackbar(t('forms.formUpdated'));
      } else {
        await api.post('/forms', formData);
        showSnackbar(t('forms.formCreated'));
      }
      navigate('/forms');
    } catch (error) {
      showSnackbar(error.response?.data?.error || t('common.error'), 'error');
    }
  };

  return (
    <Layout>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4">
          {id ? t('forms.editForm') : t('forms.createForm')}
        </Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Form Details
          </Typography>

          <TextField
            fullWidth
            label={t('forms.formName')}
            name="formName"
            value={formData.formName}
            onChange={handleChange}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label={t('forms.description')}
            name="description"
            value={formData.description}
            onChange={handleChange}
            margin="normal"
            multiline
            rows={2}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>{t('forms.status')}</InputLabel>
            <Select
              name="status"
              value={formData.status}
              onChange={handleChange}
              label={t('forms.status')}
            >
              <MenuItem value="draft">{t('forms.draft')}</MenuItem>
              <MenuItem value="active">{t('forms.active')}</MenuItem>
              <MenuItem value="inactive">{t('forms.inactive')}</MenuItem>
            </Select>
          </FormControl>
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">{t('forms.fields')}</Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddField}
            >
              {t('forms.addField')}
            </Button>
          </Box>

          {formData.fields.length === 0 ? (
            <Alert severity="info">
              No fields added yet. Click "Add Field" to create your first field.
            </Alert>
          ) : (
            <List>
              {formData.fields.map((field, index) => (
                <React.Fragment key={index}>
                  <ListItem
                    secondaryAction={
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveField(index, 'up')}
                          disabled={index === 0}
                        >
                          <ArrowUpIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveField(index, 'down')}
                          disabled={index === formData.fields.length - 1}
                        >
                          <ArrowDownIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleEditField(index)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteField(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1">
                            {field.label.en} / {field.label.mr || '(No Marathi label)'}
                          </Typography>
                          {field.required && (
                            <Chip label="Required" size="small" color="error" />
                          )}
                          {field.voiceEnabled && (
                            <Chip label="Voice" size="small" color="primary" />
                          )}
                        </Box>
                      }
                      secondary={`Type: ${field.type} | Key: ${field.key}`}
                    />
                  </ListItem>
                  {index < formData.fields.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>

        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            type="submit"
            startIcon={<SaveIcon />}
            size="large"
          >
            {t('common.save')}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/forms')}
            size="large"
          >
            {t('common.cancel')}
          </Button>
        </Box>
      </form>

      <FieldEditor
        open={fieldDialogOpen}
        field={editingField}
        onClose={() => setFieldDialogOpen(false)}
        onSave={handleSaveField}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default FormBuilder;
